import {APIResourceService} from '@/core/services/APIService'
import Collection from '@/core/resources/Collection'
import {Accessibility, VisibilitySpectrum} from '@/core/Common'
import {buildException} from '@/core/exceptions'

export class CollectionService extends APIResourceService<Collection> {
    constructor() {
        super(Collection)
    }

    /**
     * Appel API pour récupérer le fichier README d'une collection
     * @param item La collection concernée
     * @param token Jeton de session JWT
     * @throws PolluxApiError si l'upload a échoué
     */
    getReadmeFile(item: Collection, token: string) {
        return this.client
            .get('collections/' + item.name + '/get_readme', token, {}, 'blob')
            .then((response) => new File([response.data], `README_${item.name}.txt`, {type: response.headers['content-type']})
            )
            .catch((err) => {
                throw buildException(err)
            })
    }

    /**
     * Appel API pour upload le fichier README d'une collection
     * @param item La collection concernée
     * @param file fichier README
     * @param token Jeton de session JWT
     * @throws PolluxApiError si l'upload a échoué
     */
    uploadReadmeFile(item: Collection, file: any, token: string): Promise<boolean> {
        Object.defineProperty(file, 'name', {writable: true})
        file.name = 'README_' + item.name + '.txt'
        const formData = new FormData()
        formData.append('file', file)

        return this.client
            .post('collections/' + item.name + '/upload_readme', formData, token)
            .then(() => true)
            .catch((err) => {
                throw buildException(err)
            })
    }

    /**
     * Appel API pour mettre à jour la visibilité d'une liste de collections
     * @param collectionNames La liste des collections à mettre à jour
     * @param visibility La visibilité à attribuer aux collections
     * @param token Jeton de session JWT
     * @return Vrai si l'opération a réussie, Faux sinon
     * @throws PolluxApiError si la modification a échoué
     */
    updateVisibility(collectionNames: Array<string>, visibility: VisibilitySpectrum, token: string): Promise<boolean> {
        const json = {
            names: collectionNames,
            hidden: {
                [VisibilitySpectrum.DISPLAY]: '0',
                [VisibilitySpectrum.READY]: '1',
                [VisibilitySpectrum.HIDDEN]: '2'
            }[visibility]
        }
        return this.client
            .put('/collections/update_list', json, token)
            .then(() => true)
            .catch((err) => {
                throw buildException(err)
            })
    }

    /**
     * Appel API pour mettre à jour l'accessibilité d'une liste de collections
     * @param collectionNames La liste des collections à mettre à jour
     * @param token Jeton de session JWT
     * @return Vrai si l'opération a réussie, Faux sinon
     * @throws PolluxApiError si la modification a échoué
     */
    updateAccessibility(collectionNames: Array<string>, accessibility: Accessibility, token: string): Promise<boolean> {
        const json = {
            names: collectionNames,
            accessibility: accessibility == Accessibility.PUBLIC ? '1' : '0'
        }
        return this.client
            .put('/collections/update_list', json, token)
            .then(() => true)
            .catch((err) => {
                throw buildException(err)
            })
    }

    /**
     * Appel API pour créer l'archive et mettre à jour les minmax d'une collection
     * @param collection La collection a mettre à jour
     * @param token Jeton de session JWT
     * @return L'utilisateur avec les informations mise à jour
     * @throws PolluxApiError si la modification a échoué
     */
    exportCollection(collection: Collection, token: string): Promise<boolean> {
        return this.client
            .get('/collections/' + collection.name + '/export', token, null)
            .then(() => true)
            .catch((err) => {
                throw buildException(err)
            })
    }
}
